/**
 * Auth context (the value type + React context object only).
 *
 * Kept in a non-component module so the consumer hook (`useAuth`) and
 * the provider component can both export from JS files dedicated to
 * components, satisfying `react-refresh/only-export-components`.
 */
import { createContext } from "react";
import type { MeResponse } from "./api";

export interface AuthContextValue {
  user: MeResponse | null;
  loading: boolean;
  /**
   * Last non-401 error from `getMe()` (network blip, 5xx, CORS), or
   * null. 401s clear `user` and leave `error` null because they're the
   * expected "not signed in" path. Non-401s preserve the previous
   * `user` snapshot so a transient outage doesn't kick a working
   * session back to /login; surfaces can read this slot to render an
   * inline alert with a retry.
   */
  error: unknown;
  refresh: () => Promise<MeResponse | null>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);
